'use client';

import { LocationUtils } from '@ctct/rise-utils';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const getCountryCode = (countryInput: string): string => {
  const normalized = countryInput.trim().toLowerCase();

  // Already a 2-letter code
  if (normalized.length === 2) {
    const match = LocationUtils.countries.find((c) => c.abbreviation.toLowerCase() === normalized);
    if (match) return match.abbreviation.toLowerCase();
  }

  // Full name lookup
  const byName = LocationUtils.countries.find((c) => c.name.toLowerCase() === normalized);
  if (byName) return byName.abbreviation.toLowerCase();

  return normalized;
};

const getStateCode = (stateValue: string): string => {
  const state = LocationUtils.states.find(
    (s) => s.abbreviation === stateValue || s.name === stateValue
  );
  if (state) return state.abbreviation.toUpperCase();

  const province = LocationUtils.provinces.find(
    (p) => p.abbreviation === stateValue || p.name === stateValue
  );
  if (province) return province.abbreviation.toUpperCase();

  return stateValue;
};

const getStateName = (stateValue: string): string => {
  const state = LocationUtils.states.find(
    (s) => s.abbreviation === stateValue || s.name === stateValue
  );
  if (state) return state.name;

  const province = LocationUtils.provinces.find(
    (p) => p.abbreviation === stateValue || p.name === stateValue
  );
  if (province) return province.name;

  return stateValue;
};

interface AddressPrediction {
  placeId: string;
  description: string;
}

interface BusinessAddressData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface BusinessInfoCandidate {
  businessAddress: BusinessAddressData;
  formattedAddress?: string;
}

interface UseAddressAutocompleteFieldOptions {
  formKey: string;
  formKeyPrefix?: string;
  autocompleteData?: { predictions: AddressPrediction[] } | null;
  isLoadingAutocomplete?: boolean;
  businessInfoData?: { candidates: BusinessInfoCandidate[] } | null;
  isLoadingBusinessInfo?: boolean;
  onSelectPlaceId?: (placeId: string) => void;
  onSearchChange?: (query: string) => void;
  onEnterManually?: () => void;
}

const useAddressAutocompleteField = ({
  formKey,
  formKeyPrefix,
  autocompleteData,
  isLoadingAutocomplete = false,
  businessInfoData,
  isLoadingBusinessInfo = false,
  onSelectPlaceId,
  onSearchChange,
  onEnterManually,
}: UseAddressAutocompleteFieldOptions) => {
  const { setValue, clearErrors, trigger } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);
  const [isDebouncePending, setIsDebouncePending] = useState(false);

  const getKey = useCallback(
    (field: string) => (formKeyPrefix ? `${formKeyPrefix}.${field}` : field),
    [formKeyPrefix]
  );

  // Populate form fields when business info is received
  useEffect(() => {
    if (!businessInfoData?.candidates?.[0]) return;

    const candidate = businessInfoData.candidates[0];
    const address = candidate.businessAddress;
    const countryCode = getCountryCode(address.country);
    // US formattedAddress: "{street}[, {suite}], {city}, {state} {zip}, USA"
    // Other countries (e.g. UK) merge city+zip in one segment — don't parse them
    const formattedParts =
      countryCode === 'us' ? (candidate.formattedAddress?.split(', ') ?? []) : [];
    const cityFromFormattedAddress = formattedParts[address.addressLine2 ? 2 : 1] ?? '';
    const isStateSelect = countryCode === 'us' || countryCode === 'ca';

    setValue(getKey('countryCode'), countryCode, { shouldDirty: true });
    setValue(getKey('addressLine1'), address.addressLine1 || '', { shouldDirty: true });
    setValue(getKey('addressLine2'), address.addressLine2 || '', { shouldDirty: true });
    setValue(getKey('city'), address.city || cityFromFormattedAddress, { shouldDirty: true });

    if (isStateSelect) {
      setValue(getKey('stateCode'), getStateCode(address.state), { shouldDirty: true });
      setValue(getKey('stateName'), '', { shouldDirty: true });
    } else {
      setValue(getKey('stateCode'), '', { shouldDirty: true });
      setValue(getKey('stateName'), getStateName(address.state), { shouldDirty: true });
    }

    setValue(getKey('postalCode'), address.zip || '', { shouldDirty: true });

    clearErrors([
      getKey('addressLine1'),
      getKey('city'),
      getKey('stateCode'),
      getKey('stateName'),
      getKey('countryCode'),
      getKey('postalCode'),
    ]);

    void trigger([
      getKey('addressLine1'),
      getKey('city'),
      getKey('stateCode'),
      getKey('stateName'),
      getKey('countryCode'),
    ]);
  }, [businessInfoData, setValue, clearErrors, trigger, getKey]);

  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  const debouncedSearchRef = useRef(
    debounce((value: string) => {
      onSearchChangeRef.current?.(value);
      setIsDebouncePending(false);
    }, DEBOUNCE_MS)
  );

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setValue(formKey, value);

      if (value.length >= MIN_SEARCH_LENGTH) setIsDebouncePending(true);

      debouncedSearchRef.current(value);
      setIsOpen(value.length >= MIN_SEARCH_LENGTH);
    },
    [setValue, formKey]
  );

  const handleSelect = useCallback(
    (placeId: string) => {
      onSelectPlaceId?.(placeId);
      setIsOpen(false);
    },
    [onSelectPlaceId]
  );

  const handleEnterManually = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsOpen(false);
      onEnterManually?.();
    },
    [onEnterManually]
  );

  const handlePreventAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const handleClear = useCallback(() => {
    setValue(formKey, '');
    onSearchChangeRef.current?.('');
    setIsOpen(false);
  }, [formKey, setValue]);

  const predictions = autocompleteData?.predictions ?? [];

  return {
    predictions,
    isOpen,
    setIsOpen,
    isLoading: isLoadingAutocomplete,
    isDebouncePending,
    isLoadingBusinessInfo,
    handleInputChange,
    handleSelect,
    handleEnterManually,
    handlePreventAutoFocus,
    handleClear,
  };
};

export {
  type AddressPrediction,
  type BusinessAddressData,
  type BusinessInfoCandidate,
  MIN_SEARCH_LENGTH,
  useAddressAutocompleteField,
  type UseAddressAutocompleteFieldOptions,
};
